import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthorizationModule, RolesPipe } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  RoleAction,
  RoleName,
  RolePermissionSubject,
  Shift,
} from '@wilson/interfaces';
import { CanAllShiftBeModifiedPipe } from '@wilson/non-domain-specific/shift-helpers/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { TimelineMultiSelectHelperService } from '../timeline-multi-select-helper.service';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { AbilityServiceSignal } from '@casl/angular';

@Component({
  selector: 'wilson-delete-multi-select-shifts-button',
  imports: [
    CommonModule,
    NzButtonModule,
    TranslateModule,
    FeatureFlagsModule,
    CanAllShiftBeModifiedPipe,
    AuthorizationModule,
    NzTooltipModule,
  ],
  templateUrl: './delete-multi-select-shifts-button.component.html',
  styleUrl: './delete-multi-select-shifts-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TimelineMultiSelectHelperService],
})
export class DeleteMultiSelectShiftsButtonComponent {
  rolesPipe = inject(RolesPipe);
  private readonly nzModalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);

  @Input({ required: true }) selectedShifts!: (Shift & { id: string })[];
  @Input() isOperationOnGoing = false;
  @Input() hasServiceActivity = false;
  @Output() deleteOperationTriggered = new EventEmitter();

  hasCompanyAdminRole$ = this.rolesPipe.transform([RoleName.CompanyAdmin]);

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  protected async deleteShifts(): Promise<void> {
    if (await this.showDeleteMultiShiftsConfirmModal()) {
      this.deleteOperationTriggered.emit();
    }
  }

  private showDeleteMultiShiftsConfirmModal(): Promise<boolean> {
    const confirmModalRef = this.nzModalService.warning({
      nzTitle: this.translate.instant(
        'page.shift_timeline.drawer.shifts_form.delete_multi_shift',
      ),
      nzContent: this.translate.instant(
        'page.shift_timeline.drawer.delete_multi_shifts.popup',
      ),
      nzCancelText: this.translate.instant('general.cancel'),
      nzOkText: this.translate.instant('general.ok'),
      nzOkType: 'primary',
      nzOkDanger: false,
      nzCentered: true,
      nzOnOk: () => {
        confirmModalRef.close(true);
      },
    });
    return firstValueFrom(confirmModalRef.afterClose);
  }
}
